import React, { useState } from 'react';
import { Mail, Send, Eye, EyeOff, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

const EmailConfigStep = ({ formData, updateFormData }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [testEmail, setTestEmail] = useState('');

    const emailConfig = formData.emailConfig || {
        enabled: false,
        smtpHost: '',
        smtpPort: 587,
        smtpSecure: false,
        email: '',
        password: '',
        fromName: ''
    };

    const handleConfigChange = (field, value) => {
        updateFormData({
            emailConfig: {
                ...emailConfig,
                [field]: value
            }
        });
    };

    const handleTestEmail = async () => {
        if (!testEmail) {
            alert('Please enter a test email address');
            return;
        }

        setTesting(true);
        setTestResult(null);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/email-config/test-connection`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    testEmail,
                    emailConfig: emailConfig // Send current config state
                }),
            });

            const result = await response.json();

            if (result.success) {
                setTestResult({
                    success: true,
                    message: 'Test email sent successfully! Check your inbox.'
                });
            } else {
                throw new Error(result.message || 'Failed to send test email');
            }
        } catch (error) {
            setTestResult({
                success: false,
                message: error.message || 'Failed to send test email'
            });
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h2 className="text-xl font-bold text-gray-900">Email Configuration</h2>
                <p className="text-sm text-gray-600 mt-1">
                    Configure SMTP settings to send automated emails to participants
                </p>
            </div>

            {/* Enable/Disable Toggle */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Enable Email Notifications
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Send automated emails for registration confirmations, updates, and reminders
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => handleConfigChange('enabled', !emailConfig.enabled)}
                        className={`
                            relative inline-flex h-8 w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                            ${emailConfig.enabled ? 'bg-blue-600' : 'bg-gray-300'}
                        `}
                    >
                        <span
                            className={`
                                inline-block h-6 w-6 transform rounded-full bg-white transition-transform
                                ${emailConfig.enabled ? 'translate-x-9' : 'translate-x-1'}
                            `}
                        />
                    </button>
                </div>
            </div>

            {emailConfig.enabled && (
                <>
                    {/* SMTP Settings */}
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Mail className="w-5 h-5 mr-2 text-blue-600" />
                            SMTP Server Settings
                        </h3>

                        <div className="space-y-4">
                            {/* SMTP Host */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    SMTP Host <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={emailConfig.smtpHost}
                                    onChange={(e) => handleConfigChange('smtpHost', e.target.value)}
                                    placeholder="smtp.gmail.com"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* SMTP Port and Secure */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        SMTP Port <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={emailConfig.smtpPort}
                                        onChange={(e) => handleConfigChange('smtpPort', parseInt(e.target.value))}
                                        placeholder="587"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Use SSL/TLS
                                    </label>
                                    <select
                                        value={emailConfig.smtpSecure ? 'true' : 'false'}
                                        onChange={(e) => handleConfigChange('smtpSecure', e.target.value === 'true')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="false">No (Port 587)</option>
                                        <option value="true">Yes (Port 465)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Email and Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email Address <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    value={emailConfig.email}
                                    onChange={(e) => handleConfigChange('email', e.target.value)}
                                    placeholder="noreply@yourdomain.com"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Password / App Password <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={emailConfig.password}
                                        onChange={(e) => handleConfigChange('password', e.target.value)}
                                        placeholder="••••••••••••"
                                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    For Gmail, use an App Password instead of your regular password
                                </p>
                            </div>

                            {/* From Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    From Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={emailConfig.fromName}
                                    onChange={(e) => handleConfigChange('fromName', e.target.value)}
                                    placeholder="Event Organizer"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Bulk Email CC */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Bulk Upload Email CC <span className="text-gray-400 font-normal">(optional)</span>
                                </label>
                                <input
                                    type="email"
                                    value={emailConfig.bulkEmailCc || ''}
                                    onChange={(e) => handleConfigChange('bulkEmailCc', e.target.value)}
                                    placeholder="e.g. admin@example.com"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Every confirmation email sent during Bulk Upload will CC this address.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Test Email */}
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Send className="w-5 h-5 mr-2 text-blue-600" />
                            Test Email Configuration
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Test Email Address
                                </label>
                                <input
                                    type="email"
                                    value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                    placeholder="test@example.com"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <button
                                type="button"
                                onClick={handleTestEmail}
                                disabled={testing || !emailConfig.smtpHost || !emailConfig.email || !emailConfig.password}
                                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                            >
                                {testing ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        Sending Test Email...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-5 h-5 mr-2" />
                                        Send Test Email
                                    </>
                                )}
                            </button>

                            {testResult && (
                                <div className={`p-4 rounded-lg flex items-start ${testResult.success
                                    ? 'bg-green-50 border border-green-200'
                                    : 'bg-red-50 border border-red-200'
                                    }`}>
                                    {testResult.success ? (
                                        <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                                    ) : (
                                        <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                                    )}
                                    <p className={`text-sm ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                                        {testResult.message}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Info Box */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex">
                            <AlertCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-800">
                                <p className="font-semibold mb-1">Important Notes:</p>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>For Gmail, enable 2-factor authentication and create an App Password</li>
                                    <li>Passwords are encrypted before storage (security best practice)</li>
                                    <li>Test your configuration before saving the event</li>
                                    <li>Email templates can be configured in the next step</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default EmailConfigStep;
